import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { HStack } from '../../layout';
import { SearchField } from '../text';
import { FilterDropdown, FilterMultiDropdown } from '../Filters';
import DateRangeFilter from '../Dates/DateRangeFilter';
import type { Range } from '../Dates';
import { RadioGroup } from '../Radio';
import { isDateRangeValue } from './types';
import type { CommonFiltrationProps, Filter } from './types';

const FilterContainerComponent = memo(
    ({
        styles,
        children,
    }: {
        /**
         * Filter styles
         */
        styles: Filter['styles'];
        children: ReactNode;
    }) => {
        return <div style={styles}>{children}</div>;
    }
);

const FilterContainer = memo(FilterContainerComponent);

export const dateRanges = [
    {
        label: 'LastWeek',
        tentativeStartDate: DateTime.local()
            .startOf('day')
            .minus({ week: 1 })
            .toJSDate(),
        tentativeEndDate: new Date(),
    },
    {
        label: 'Last30Days',
        tentativeStartDate: DateTime.local()
            .startOf('day')
            .minus({ days: 30 })
            .toJSDate(),
        tentativeEndDate: new Date(),
    },
    {
        label: 'ThisYear',
        tentativeStartDate: DateTime.local()
            .startOf('day')
            .startOf('year')
            .toJSDate(),
        tentativeEndDate: new Date(),
    },
    {
        label: 'LastYear',
        tentativeStartDate: DateTime.local()
            .startOf('day')
            .minus({ year: 1 })
            .toJSDate(),
        tentativeEndDate: new Date(),
    },
];

const Filters = <T,>({
    filters,
    filterValue,
    onChangeFilters,
}: Pick<
    CommonFiltrationProps<T>,
    'filters' | 'filterValue' | 'onChangeFilters'
>) => {
    const intl = useIntl();
    const predefinedDateRanges = useMemo(
        () =>
            dateRanges.map(item => {
                return {
                    ...item,
                    label: intl.formatMessage({
                        id: `Library Filtration DateRange ${item.label}`,
                    }),
                };
            }),
        [intl]
    );

    return (
        <HStack gap="mini" wrap align="center">
            {filters.map(item => {
                const currentValue = filterValue[item.field];

                const dataTestClass = `filtration-${item.field}`;

                switch (item.type) {
                    case 'search': {
                        return (
                            <FilterContainer
                                styles={item.styles}
                                key={item.field}
                            >
                                <SearchField
                                    placeholder={item.placeholder}
                                    data-test-class={dataTestClass}
                                    value={currentValue as string}
                                    onChange={value => {
                                        onChangeFilters({
                                            ...filterValue,
                                            [item.field]: value,
                                        });
                                    }}
                                />
                            </FilterContainer>
                        );
                    }
                    case 'multiSelect': {
                        return (
                            <FilterContainer
                                styles={item.styles}
                                key={item.field}
                            >
                                <FilterMultiDropdown
                                    data-test-class={dataTestClass}
                                    searchable={item.searchable}
                                    noSelectedLabels
                                    selectedCountBadge
                                    id="brandSelect"
                                    matchWidth={item.matchWidth}
                                    placeholder={item.placeholder}
                                    value={
                                        currentValue
                                            ? (currentValue as string[])
                                            : []
                                    }
                                    onChange={value => {
                                        onChangeFilters({
                                            ...filterValue,
                                            [item.field]: value,
                                        });
                                    }}
                                    windowRender={item.windowRender}
                                    options={item.items}
                                    disabled={item.items.length === 0}
                                    clearable
                                />
                            </FilterContainer>
                        );
                    }
                    case 'select': {
                        return (
                            <FilterContainer
                                styles={item.styles}
                                key={item.field}
                            >
                                <FilterDropdown
                                    data-test-class={dataTestClass}
                                    id={dataTestClass}
                                    placeholder={item.placeholder}
                                    searchable={item.searchable}
                                    matchWidth={item.matchWidth}
                                    value={currentValue as string}
                                    onChange={value => {
                                        onChangeFilters({
                                            ...filterValue,
                                            [item.field]: value,
                                        });
                                    }}
                                    options={item.items}
                                    windowRender={item.windowRender}
                                    success={!!currentValue}
                                    disabled={item.items.length === 0}
                                    clearable
                                />
                            </FilterContainer>
                        );
                    }
                    case 'radio': {
                        return (
                            <FilterContainer
                                styles={item.styles}
                                key={item.field}
                            >
                                <RadioGroup
                                    value={(currentValue as string) || ''}
                                    onChange={value => {
                                        onChangeFilters({
                                            ...filterValue,
                                            [item.field]: value,
                                        });
                                    }}
                                    button
                                    options={item.items}
                                />
                            </FilterContainer>
                        );
                    }
                    case 'dateRange': {
                        const value = isDateRangeValue(currentValue)
                            ? ([
                                  DateTime.fromISO(
                                      currentValue.startDate
                                  ).toJSDate(),
                                  DateTime.fromISO(
                                      currentValue.endDate
                                  ).toJSDate(),
                              ] as Range<Date>)
                            : undefined;

                        return (
                            <FilterContainer
                                styles={item.styles}
                                key={item.field}
                            >
                                <DateRangeFilter
                                    value={value}
                                    predefinedOptions={predefinedDateRanges}
                                    placeholder={item.placeholder}
                                    onChange={range => {
                                        onChangeFilters({
                                            ...filterValue,
                                            [item.field]: {
                                                startDate: range
                                                    ? range[0].toISOString()
                                                    : undefined,
                                                endDate: range
                                                    ? range[1].toISOString()
                                                    : undefined,
                                            },
                                        });
                                    }}
                                    calendarProps={{
                                        minDate: item.minDate,
                                    }}
                                />
                            </FilterContainer>
                        );
                    }
                    case 'custom': {
                        return (
                            <FilterContainer
                                styles={item.styles}
                                key={item.field}
                            >
                                {item.component}
                            </FilterContainer>
                        );
                    }
                    default: {
                        return <div />;
                    }
                }
            })}
        </HStack>
    );
};

// eslint-disable-next-line @hostettler/memoize-component
export default memo(Filters) as unknown as typeof Filters;
